import { handleApiError } from "../utils/apiUtils";
import api from "./Api";
import { AxiosProgressEvent, CancelToken } from "axios";

const Case = process.env.REACT_APP_CASE_URL || "";
if (!Case) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const caseAPI = api(Case);

export interface ApiResponse<T> {
  id?: number;
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

interface DocumentCategory {
  documentTypeId: number;
  documentType: string;
  documentCategory: string;
  documentTypeDescription: string;
}

interface DocumentCategoryItem {
  documentTypeId: number | string;
  documentType: string;
  documentCategory: string;
  documentTypeDescription: string;
}

interface DocumentTypeOption {
  value: string;
  label: string;
  documentCategory: string;
  documentTypeDescription: string;
}

interface ProvisionalId {
  provisionId: number | null;
  caseRecordDetailsId: number | null;
  action: string;
}

interface RecordPayloadUpdate {
  caseRecordHeaderId?: number;
  caseInfoId: number;
  documentTypeId: number | null | string;
  additionalComments: string;
  provisionalIds: ProvisionalId[];
  createdBy?: number | null;
  modifiedBy?: number | null;
}

interface RecordPayload {
  caseRecordHeaderId?: number;
  caseInfoId: number;
  documentTypeId: number | null | string;
  additionalComments: string;
  provisionalIds: string;
  createdBy?: number | null;
  modifiedBy?: number | null;
}

interface QME110Details {
  caseInfoId: number;
  caseSubjectId: number;
  employeeName: string;
  employeePhoneNumber: string | null;
  employeeStreetAddress: string;
  employeeCity: string | null;
  employeeState: string | null;
  employeeZipCode: string | null;
  caseSubjectInjuryId: number;
  injuryTypeId: number;
  injuryTypeDesc: string | null;
  dateOfInjury: string;
  doictStartDate: string;
  doictEndDate: string;
  panelNumber: string;
  claimNumber: string | null;
  employerStakeholderId: number;
  employerName: string;
  employerAddressLine1: string;
  employerCity: string;
  employerState: string;
  employerZipCode: string;
  claimInsuranceId: number;
  caseStkContactSalutationTypeDesc: string | null;
  caseStkContactFirstName: string | null;
  caseStkContactMiddleName: string | null;
  caseStkContactLastName: string | null;
  insPhoneNumber: string | null;
  insuranceCompanyName: string | null;
  insAddressLine1: string | null;
  insAddressLine2: string | null;
  insAddressLine3: string | null;
  insCity: string | null;
  insState: string | null;
  insZipCode: string | null;
  subjectQmeScheduleId: number;
  dateOfAppointmentCall: string;
  dateOfAppointment: string;
  timeOfAppointment: string;
  examAddress: string;
  examCity: string;
  examState: string;
  examZip: string;
  sendRecordsToAddress: string | null;
  sendRecordsToCity: string | null;
  sendRecordsToState: string | null;
  sendRecordsToZipCode: string | null;
  isInterpreterRequired: number;
  interpreterLanguageDesc: string;
  qmeNameProviderName: string;
  qmeAddress: string;
  qmeCity: string;
  qmeState: string;
  qmeZip: string;
  dateSign: string;
  qmeDocFullName: string;
  qmeDosAddress: string | null;
  qmeDosCity: string | null;
  qmeDosState: string | null;
  qmeDosZipCode: string | null;
  qmeDosDate: string;
  qmeFormSignatoryDate: string;
  qmeFormSignatoryCity: string | null;
  qmeFormSignatoryName: string | null;
  qmeFormSignatorySign: string | null;
  patientName: string | null;
  patientStreetAddress: string | null;
  patientCity: string | null;
  patientState: string | null;
  patientZipCode: string | null;
  appAttName: string | null;
  appAttStreetAddress: string | null;
  appAttCity: string | null;
  appAttState: string | null;
  appAttZipCode: string | null;
  defAttName: string | null;
  defAttStreetAddress: string | null;
  defAttCity: string | null;
  defAttState: string | null;
  defAttZipCode: string | null;
  claimInsName: string | null;
  claimInsStreetAddress: string | null;
  claimInsCity: string | null;
  claimInsState: string | null;
  claimInsZipCode: string | null;
  responseCode: number;
  responseMessage: string;
}

interface UploadRecordResult {
  id: number;
  responseCode: number;
  responseMessage: string;
  noofPages?: number;
  cloudStoragePath?: string;
}

interface UploadChunkPayload {
  fileName: string;
  totalChunks: number;
  originalFileName: string;
  caseInfoId: number;
  description: string;
  additionalFileInfo: string;
}

interface AddressDetail {
  caseRecordDetailsID: number;
  fileName: string;
  storageLocation: string;
  cloudLocation: string;
  uploadDateTime: string;
  uploadErrorCode: string;
  uploadErrorMessage: string;
  description: string;
  uploadedBy: number;
  uploadedByName: string;
  detailIsActive: number;
  detailCreatedBy: number;
  detailCreatedByName: string;
  detailCreatedDate: string;
  detailModifiedBy: number;
  detailModifiedByName: string;
  detailModifiedDate: string;
}

interface RecordHeaderDetails {
  caseRecordHeaderId: number;
  caseInfoId: number;
  documentTypeId: number;
  documentTypeName: string;
  documentCategory: string;
  senderKeyId?: number;
  senderKeyName?: string;
  senderKeyCode?: string;
  documentTypeDescription: string;
  additionalComments: string;
  headerIsActive: number;
  headerCreatedBy: number;
  headerCreatedByName: string | null;
  headerCreatedDate: string;
  headerModifiedBy: number;
  headerModifiedByName: string;
  headerModifiedDate: string;
  parentCaseRecordDetailsId: number;
  attachments: string;
  addressesJson: AddressDetail[];
  subjectQMEScheduleID: number;
  dateofAppointment: string;
  responseCode: number;
  responseMessage: string;
}

interface RecordSummary {
  keyId: number | null;
  datacategory: string;
  documentCategory: string;
  totalRecords: number;
  lastUploadedBy: string;
  lastUploadedOn: string;
  responseCode: number;
  responseMessage: string;
}

interface ListRecord {
  caseRecordHeaderId: number;
  caseInfoId: number;
  documentCategory: string;
  documentTypeId: number;
  documentTypeName: string;
  additionalComments: string;
  headerCreatedBy: string;
  headerModifiedByName: string;
  headerCreatedDate: string;
  totalNoOfFiles: number;
  responseCode: number;
  responseMessage: string;
  dateOfAppointment?: any;
  caseTypeExamType?: any;
}

interface CaseRecordPopupDetails {
  caseRecordHeaderId: number;
  caseRecordDetailsId: number;
  fileName: string;
  cloudLocation: string;
  description: string;
  uploadedBy: number;
  uploadedByName: string;
  uploadDateTime: string;
  uploadDateTimeNew?: string;
  download?: string;
}

const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-US");
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};

const fetchDocumentCategoryList = async (): Promise<DocumentTypeOption[]> => {
  try {
    const response = await caseAPI.get("/api/case/record/document/type/list");
    const list = response.data?.result.map((item: DocumentCategoryItem) => ({
      value: item.documentTypeId,
      label: item.documentType,
      documentCategory: item.documentCategory,
      documentTypeDescription: item.documentTypeDescription,
    }));
    return list;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchDocumentTypeListForReviewer = async (): Promise<
  DocumentTypeOption[]
> => {
  try {
    const response = await caseAPI.get(
      "/api/case/record/document/type/list-for-reviewer",
    );
    const list = response.data?.result.map((item: DocumentCategoryItem) => ({
      value: item.documentTypeId,
      label: item.documentType,
      documentCategory: item.documentCategory,
      documentTypeDescription: item.documentTypeDescription,
    }));
    return list;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
const fetchDocumentTypeListForHRE = async (): Promise<DocumentTypeOption[]> => {
  try {
    const response = await caseAPI.get(
      "/api/case/notes/document/type/list-for-hre",
    );
    const list = response.data?.result.map((item: DocumentCategoryItem) => ({
      value: item.documentTypeId,
      label: item.documentType,
      documentCategory: item.documentCategory,
      documentTypeDescription: item.documentTypeDescription,
    }));
    return list;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getRecordHeaderdetails = async (
  recordHeaderId: number,
): Promise<ApiResponse<RecordHeaderDetails[]>> => {
  try {
    const response = await caseAPI.get<ApiResponse<RecordHeaderDetails[]>>(
      `/api/case/record/header/details?caseRecordHeaderId=${recordHeaderId}`,
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const uploadChunkRecord = async (
  formData: FormData,
  options: { cancelToken?: CancelToken; signal?: AbortSignal } = {},
): Promise<ApiResponse<UploadRecordResult[]>> => {
  try {
    const response = await caseAPI.post<ApiResponse<UploadRecordResult[]>>(
      "/api/case/record/upload-chunk",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        cancelToken: options.cancelToken,
        signal: options.signal,
      },
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const uploadChunkRecordFinal = async (
  payload: UploadChunkPayload,
  cancelToken?: CancelToken,
): Promise<ApiResponse<UploadRecordResult[]>> => {
  try {
    const response = await caseAPI.post<ApiResponse<UploadRecordResult[]>>(
      "/api/case/record/finalize-upload",
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
        cancelToken,
      },
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const uploadPageCount = async (formData: FormData): Promise<any> => {
  try {
    const response = await caseAPI.post(
      "/api/case/record/update-page-count",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const addUploadRecordFiles = async (
  formData: FormData,
  onUploadProgress?: (percentCompleted: number) => void,
  cancelToken?: CancelToken,
): Promise<ApiResponse<UploadRecordResult[]>> => {
  try {
    const response = await caseAPI.post<ApiResponse<UploadRecordResult[]>>(
      "/api/case/record/provisional/add",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onUploadProgress(percentCompleted);
          }
        },
        cancelToken,
      },
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
interface UploadNoteResult {
  id: number;
  responseCode: number;
  responseMessage: string;
  cloudStoragePath: string;
}

export const addCaseNotesAttachments = async (
  formData: FormData,
  onUploadProgress?: (percentCompleted: number) => void,
): Promise<ApiResponse<UploadNoteResult[]>> => {
  try {
    const response = await caseAPI.post<ApiResponse<UploadNoteResult[]>>(
      "/api/case/notes/provisional/add",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onUploadProgress(percentCompleted);
          }
        },
      },
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateUploadRecordFiles = async (
  formData: FormData,
  onUploadProgress?: (percentCompleted: number) => void,
  cancelToken?: CancelToken,
): Promise<ApiResponse<UploadRecordResult>> => {
  try {
    const response = await caseAPI.post<ApiResponse<UploadRecordResult>>(
      "/api/case/record/provisional/update",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onUploadProgress(percentCompleted);
          }
        },
        cancelToken,
      },
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result, id: result?.id ?? "" };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const deleteUploadRecordFile = async (
  provisionalAttachmentId: number,
  fileName: string,
): Promise<ApiResponse<UploadRecordResult>> => {
  try {
    const response = await caseAPI.delete<ApiResponse<UploadRecordResult>>(
      `/api/case/record/provisional/delete?provisionalAttachmentId=${provisionalAttachmentId}&fileName=${fileName}`,
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result, id: result?.id ?? "" };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const addUploadRecordHeader = async (
  formData: RecordPayload,
): Promise<ApiResponse<UploadRecordResult>> => {
  try {
    const response = await caseAPI.post<ApiResponse<UploadRecordResult>>(
      "/api/case/record/header/add",
      formData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateUploadRecordHeader = async (
  formData: RecordPayloadUpdate,
): Promise<ApiResponse<UploadRecordResult>> => {
  try {
    const response = await caseAPI.post<ApiResponse<UploadRecordResult>>(
      "/api/case/record/revisions/update",
      formData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getQME110Details = async (
  qmeScheduleId: string,
): Promise<ApiResponse<QME110Details>> => {
  try {
    const response = await caseAPI.get<ApiResponse<QME110Details>>(
      `/api/appointment/qme110/details`,
      { params: { qmeScheduleId } },
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getRecordListCountDetails = async (
  caseInfoId: number,
  groupType?: number,
  searchString: string = "",
  qmeScheduleId: any = "",
): Promise<ApiResponse<RecordSummary[]>> => {
  try {
    const response = await caseAPI.get<ApiResponse<RecordSummary[]>>(
      `/api/case/record/summary?caseInfoId=${caseInfoId}&groupType=${groupType}&searchString=${searchString}&qmeScheduleId=${qmeScheduleId}`,
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getRecordListData = async (
  caseInfoId: number,
  documentCategoryId: string,
  keyId: number,
  searchString: string = "",
  qmeScheduleId: any = "",
): Promise<ApiResponse<ListRecord[]>> => {
  const payload = {
    caseInfoId: caseInfoId,
    keyId: keyId,
    documentCategory: documentCategoryId,
    searchString: searchString,
    qmeScheduleId: qmeScheduleId ? qmeScheduleId : null,
  };

  try {
    const response = await caseAPI.post<ApiResponse<ListRecord[]>>(
      "/api/case/record/list",
      payload,
    );

    let counter = 0;
    const validDocTypes = [
      "QME Draft Report",
      "Preliminary Record Review Summary",
      "Preliminary Injured Worker History Report",
    ];

    return {
      ...response.data,
      result:
        response.data?.result?.map((item) => {
          let latestPosition: string | number = "";

          if (validDocTypes.includes(item?.documentTypeName)) {
            counter++;
            latestPosition = counter;
          }

          return {
            ...item,
            headerUpdatedDate: formatDateTime(item.headerCreatedDate),
            dateOfAppointmentInfo: `${formatDateTime(item.dateOfAppointment)} | ${item?.caseTypeExamType
              }`,
            fileAction: item.totalNoOfFiles,
            latestPosition,
            actions: { Edit: "Edit" },
          };
        }) || [],
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getRecordListPopUpDetails = async (
  caseRecordHeaderId: number,
  tagType: string,
): Promise<ApiResponse<CaseRecordPopupDetails[]>> => {
  try {
    const response = await caseAPI.get<ApiResponse<CaseRecordPopupDetails[]>>(
      `/api/case/record/list/popup?caseRecordHeaderId=${caseRecordHeaderId}&tagType=${tagType}`,
    );
    return {
      ...response.data,
      result:
        response.data.result?.map((item) => ({
          ...item,
          uploadDateTimeNew: formatDateTime(item.uploadDateTime),
          download: item.cloudLocation,
          id: item.caseRecordDetailsId,
        })) || [],
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getVersionList = async (
  caserecorddetailsIds: number | string,
): Promise<ApiResponse<CaseRecordPopupDetails[]>> => {
  try {
    const response = await caseAPI.get<ApiResponse<CaseRecordPopupDetails[]>>(
      `/api/case/record/revisions?caserecorddetailsIds=${caserecorddetailsIds}`,
    );
    return {
      ...response.data,
      result:
        response.data.result?.map((item) => ({
          ...item,
          uploadDateTimeNew: formatDateTime(item.uploadDateTime),
          download: item.cloudLocation,
        })) || [],
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getDownloadFileForPopUpDetails = async (
  caserecorddetailsId: number,
  fileNameNew: string,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await caseAPI.get(
      `/api/case/record/download?caserecorddetailsId=${caserecorddetailsId}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : fileNameNew;

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const downloadZipFileForPopUpDetails = async (
  caserecorddetailsIds: string,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await caseAPI.get(
      `/api/case/record/download-zip/${caserecorddetailsIds}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : "download.zip";

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const downloadZipFileForHeader = async (
  caserecordheaderIds: string,
  groupType: number,
  searchString: string = "",
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await caseAPI.get(
      `/api/case/record/download-zip/header?caserecordheaderIds=${caserecordheaderIds}&groupType=${groupType}&searchString=${searchString}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : "download.zip";

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const downloadZipFileForDocumentCategory = async (
  caseInfoIdNew: number,
  groupType: number,
  searchString: string = "",
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await caseAPI.get(
      `/api/case/record/download-zip/documentcategory?caseInfoId=${caseInfoIdNew}&groupType=${groupType}&searchString=${searchString}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : "download.zip";

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchDocumentCategory = async (): Promise<DocumentTypeOption[]> => {
  try {
    const response = await caseAPI.get("/api/case/record/document/category");

    // Create a map to store unique document categories
    const uniqueCategoriesMap = new Map<string, DocumentCategoryItem>();

    response.data?.result.forEach((item: DocumentCategoryItem) => {
      if (!uniqueCategoriesMap.has(item.documentCategory)) {
        uniqueCategoriesMap.set(item.documentCategory, item);
      }
    });

    // Convert map values to the expected type
    const formattedCategories: DocumentTypeOption[] = Array.from(
      uniqueCategoriesMap.values(),
    ).map((item) => ({
      CategaryID: item.documentTypeId,
      value: item.documentCategory,
      label: item.documentCategory,
      documentCategory: item.documentCategory, // Ensure required fields are included
      documentTypeDescription: item.documentTypeDescription, // Include this field as it's required
    }));

    return formattedCategories;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getRecordAttachmentsByCase = async (
  recordData: any
): Promise<any> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      `/api/case/record/attachments/by-qmescheduleid`,
      recordData
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,

      download: data.cloudLocation,
      uploadedByName: `${data?.uploadedByName} | ${formatDateTime(data.uploadDateTime)}`,
      dateOfAppointmentInfo: `${formatDateTime(data.dateOfAppointment)} | ${data?.appointmentTypeDesc
        }`,
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error getting Record Attachment list:", error);
    throw new Error(
      "Failed to get the data. Please check the data and try again.",
    );
  }
};

const getRecordAttachmentsSummaryByCase = async (
  recordData: any
): Promise<any> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      `/api/case/record/attachments/by-caseid`,
      recordData
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,

      download: data.cloudLocation,
      uploadedByName: `${data?.uploadedByName} | ${formatDateTime(data.uploadDateTime)}`,
      dateOfAppointmentInfo: `${formatDateTime(data.dateOfAppointment)} | ${data?.appointmentTypeDesc
        }`,
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error getting Record Attachment list:", error);
    throw new Error(
      "Failed to get the data. Please check the data and try again.",
    );
  }
};

export {
  fetchDocumentCategoryList,
  fetchDocumentTypeListForReviewer,
  fetchDocumentTypeListForHRE,
  uploadChunkRecordFinal,
  uploadChunkRecord,
  addUploadRecordFiles,
  updateUploadRecordFiles,
  deleteUploadRecordFile,
  addUploadRecordHeader,
  updateUploadRecordHeader,
  getRecordHeaderdetails,
  getQME110Details,
  getRecordListCountDetails,
  getRecordListData,
  getRecordListPopUpDetails,
  getDownloadFileForPopUpDetails,
  downloadZipFileForPopUpDetails,
  downloadZipFileForHeader,
  downloadZipFileForDocumentCategory,
  fetchDocumentCategory,
  getVersionList,
  uploadPageCount,
  getRecordAttachmentsByCase,
  getRecordAttachmentsSummaryByCase
};
